import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  FormControlProps,
  FormGroup,
  Form,
  Button,
  ProgressBar,
} from 'react-bootstrap';
import { formatBytes } from '../../utils/utils';
import CircledIcon from '../CircledIcon';

export type FileInputProps = FormControlProps & {
  label?: string;
  buttonName?: string;
  width?: string;
  accept?: string;
  maxFileSize?: number;
  onValidate?: (isValid: boolean) => void;
};

const FileInput: React.FC<FileInputProps> = ({
  label,
  buttonName = 'Choose file',
  placeholder,
  disabled,
  onChange,
  onMouseDown,
  isValid: isValidProp,
  isInvalid: isInvalidProp,
  onValidate,
  width = '100%',
  className,
  accept = '.pdf',
  value,
  maxFileSize = 5242880,
  ...props
}) => {
  const [fileName, setFileName] = React.useState<any>({});
  const [hasError = isInvalidProp, setHasError] =
    React.useState<boolean>(false);
  const [isValid = isValidProp, setIsValid] = React.useState<boolean>(false);
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [progress, setProgress] = React.useState<number>(0);
  const [isUploaded, setIsUploaded] = React.useState(false); //Temporary state/flag for uploaded files. No need if you have backend response for uploaded file.

  const { prefix, sufix, fileFullName, fileSize } = fileName;

  const handleClick = () => {
    if (hiddenFileInput && hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileAdded = event.target!.files?.[0];
    if (fileAdded && fileAdded.size > maxFileSize) {
      setHasError(true);
      setIsValid(false);
    } else {
      setIsValid(true);
      setHasError(false);

      //Tempory solution to animate progress bar.
      //Will be replaced with real progress for real file upload when we get some end point.
      for (let i = 0; i <= 100; i++) {
        setTimeout(() => {
          setProgress(i);
        }, 750);
      }
    }

    if (progress > 0) {
      setProgress(0);
      setIsUploaded(false);
    }

    if (fileAdded) {
      event.target.value = '';
      const fileFullName = fileAdded.name;
      const fileSize = formatBytes(fileAdded.size);
      const index = fileFullName.lastIndexOf('.');
      const beforeDot = fileFullName.slice(0, index);
      const afterDot = fileFullName.slice(index + 1);

      let updatedFileName = { prefix: beforeDot, sufix: afterDot };

      setFileName({
        ...fileName,
        ...updatedFileName,
        fileFullName,
        fileSize,
      });
    }

    onChange && onChange(event);
  };

  //Tempory condition to simulate uploaded file and hide the progress bar. Need to removed when we get real response for uploaded file.
  if (progress === 100) {
    setTimeout(() => {
      setIsUploaded(true);
    }, 1500);
  }

  useEffect(() => {
    onValidate && onValidate(isValid!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const removeAddedFile = () => {
    setFileName({});
    setHasError(false);
    setIsValid(false);
    setProgress(0);
    setIsUploaded(false);
  };

  return (
    <Fragment>
      <div className={'d-flex flex-column'}>
        <FormGroup
          className={`${className ? className : ''} file-input-custom`}
          style={{ width: width }}
        >
          {label && <Form.Label>{label}</Form.Label>}
          <FormControl
            type={'file'}
            value={value}
            onChange={handleChange}
            ref={hiddenFileInput}
            style={{ display: 'none' }}
            accept={accept}
            isValid={isValid}
            isInvalid={hasError}
            {...props}
          />
          <Button
            disabled={disabled}
            onClick={handleClick}
            bsPrefix={'form-control'}
          >
            <span className="button-name">{buttonName}</span>
            {Object.keys(fileName).length > 0 ? (
              <span className="file-name">
                <span className="truncate">{prefix}</span>
                {`.${sufix}`}
              </span>
            ) : (
              <span className="file-placeholder">{placeholder}</span>
            )}
          </Button>
        </FormGroup>

        {fileFullName && (
          <div
            className={'progress-wrapper mt-9 d-flex'}
            style={{ maxWidth: `${width}` }}
          >
            <CircledIcon
              icon={hasError ? light('file-exclamation') : light('file-pdf')}
              variant={hasError ? 'icon-danger' : isUploaded ? 'icon-success': 'icon-blue'}
              size={'icon-sm'}
            />
            <div className={'ms-7 w-100 d-flex flex-column justify-content-center'}>
              <div className={'d-flex justify-content-between'}>
                <span
                  className={`d-inline-flex ${
                    hasError
                      ? 'text-danger'
                      : isUploaded
                      ? 'text-success'
                      : 'text-indigo-darker'
                  } fw-normal text-uppercase`}
                >
                  <span className={'truncate'}>{prefix}</span>
                  {`.${sufix}`}
                </span>
                <div className={'ms-3 close-btn'} onClick={removeAddedFile}>
                  <FontAwesomeIcon icon={light('xmark')} />
                </div>
              </div>
              {!isUploaded && (
                <span className={'mt-2 text-caption text-light-grey fw-light'}>
                  {fileSize}
                </span>
              )}
              {!hasError && !isUploaded && (
                <ProgressBar
                  className={'mt-4'}
                  bsPrefix={'cnd progress'}
                  now={progress}
                />
              )}
            </div>
          </div>
        )}
        {!isUploaded ? (
          <span
            className={`mt-6 fw-light text-caption ${
              hasError ? 'ms-13 ps-1 text-danger-dark' : `${fileFullName && 'ms-13 ps-1'} text-light-grey`
            }`}
          >
            {'File size should not exceed 5MB'}
          </span>
        ) : (<span className={'ms-13 ps-1 mt-6 fw-light text-light-grey text-caption'}>{'File uploaded'}</span>)}
      </div>
    </Fragment>
  );
};

export default FileInput;
